
// BMI and Fat Loss Calculation Utilities

export interface BMIData {
  height: number; // in cm
  weight: number; // in kg
  age: number;
  gender: 'male' | 'female';
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very-active';
}

export interface BMIResults {
  bmi: number;
  category: string;
  categoryColor: string;
  healthyWeightRange: { min: number; max: number };
  targetWeight: number;
  fatToLose: number;
  caloriesToBurn: number;
  bmr: number;
  fastingEstimates: {
    protocol: string;
    hoursPerDay: number;
    daysToGoal: number;
    weeksToGoal: number;
  }[];
}

// Convert feet and inches to cm
export const convertFeetInchesToCm = (feet: number, inches: number): number => {
  return (feet * 12 + inches) * 2.54;
};

// Convert lbs to kg
export const convertLbsToKg = (lbs: number): number => {
  return lbs * 0.453592;
};

// Convert kg to lbs
export const convertKgToLbs = (kg: number): number => {
  return kg / 0.453592;
};

// Calculate BMI
export const calculateBMI = (weightKg: number, heightCm: number): number => {
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
};

// Get BMI category and color
export const getBMICategory = (bmi: number): { category: string; color: string } => {
  if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600' };
  if (bmi < 25) return { category: 'Normal weight', color: 'text-green-600' };
  if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600' };
  return { category: 'Obese', color: 'text-red-600' };
};

// Calculate healthy weight range (BMI 18.5-24.9)
export const getHealthyWeightRange = (heightCm: number): { min: number; max: number } => {
  const heightM = heightCm / 100;
  const minWeight = 18.5 * (heightM * heightM);
  const maxWeight = 24.9 * (heightM * heightM);
  return { min: minWeight, max: maxWeight };
};

// Calculate target weight (middle of healthy BMI range)
export const getTargetWeight = (heightCm: number): number => {
  const heightM = heightCm / 100;
  return 21.7 * (heightM * heightM); // BMI of 21.7 is middle of healthy range
};

// Calculate BMR using Mifflin-St Jeor Equation
export const calculateBMR = (weightKg: number, heightCm: number, age: number, gender: 'male' | 'female'): number => {
  const baseRate = 10 * weightKg + 6.25 * heightCm - 5 * age;
  return gender === 'male' ? baseRate + 5 : baseRate - 161;
};

// Apply activity multiplier to BMR
export const getTDEE = (bmr: number, activityLevel: string): number => {
  const multipliers = {
    'sedentary': 1.2,
    'light': 1.375,
    'moderate': 1.55,
    'active': 1.725,
    'very-active': 1.9
  };
  return bmr * (multipliers[activityLevel as keyof typeof multipliers] || 1.2);
};

// Calculate fasting duration estimates
export const calculateFastingEstimates = (caloriesToBurn: number, bmr: number) => {
  const protocols = [
    { name: '16:8 (16hr fast)', fastingHours: 16, eatingWindow: 8 },
    { name: '18:6 (18hr fast)', fastingHours: 18, eatingWindow: 6 },
    { name: '20:4 (20hr fast)', fastingHours: 20, eatingWindow: 4 },
    { name: 'OMAD (23hr fast)', fastingHours: 23, eatingWindow: 1 }
  ];

  return protocols.map(protocol => {
    // During fasting, body burns approximately 50-70% of BMR in additional calories
    const fastingBurnRate = bmr * 0.6 * (protocol.fastingHours / 24);
    const dailyDeficit = fastingBurnRate;
    const daysToGoal = Math.ceil(caloriesToBurn / dailyDeficit);
    const weeksToGoal = Math.ceil(daysToGoal / 7);

    return {
      protocol: protocol.name,
      hoursPerDay: protocol.fastingHours,
      daysToGoal,
      weeksToGoal
    };
  });
};

// Main calculation function
export const calculateBMIResults = (data: BMIData): BMIResults => {
  const bmi = calculateBMI(data.weight, data.height);
  const { category, color } = getBMICategory(bmi);
  const healthyWeightRange = getHealthyWeightRange(data.height);
  const targetWeight = getTargetWeight(data.height);
  const fatToLose = Math.max(0, data.weight - targetWeight);
  const caloriesToBurn = fatToLose * 3500; // 3500 calories per pound of fat
  const bmr = calculateBMR(data.weight, data.height, data.age, data.gender);
  const fastingEstimates = calculateFastingEstimates(caloriesToBurn, bmr);

  return {
    bmi,
    category,
    categoryColor: color,
    healthyWeightRange,
    targetWeight,
    fatToLose,
    caloriesToBurn,
    bmr,
    fastingEstimates
  };
};
